import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Eye, Loader2 } from 'lucide-react';
import { Pagination } from '../../../../../shared/components/Pagination';
import { useGetKvsResult9n11SchoolDetailQuery } from '../../../services/kvsResult9n11Api';
import { CLASS_NAMES } from '../data/index';

const PAGE_SIZE = 10;

const competencyColor: Record<string, string> = {
  'Excellent': 'bg-emerald-100 text-emerald-700',
  'Good': 'bg-blue-100 text-blue-700',
  'Average': 'bg-amber-100 text-amber-700',
  'Below Average': 'bg-red-100 text-red-700',
};

const StudentList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data: schoolRes, isLoading } = useGetKvsResult9n11SchoolDetailQuery({
    year: 2026,
    term: 0,
    kvid: 2188,
  });

  const mockStudents = useMemo(() => {
    const byClass = schoolRes?.data?.byClass;
    if (!byClass) return [];
    return Object.entries(byClass).flatMap(([classKey, classData]) => {
      const className = CLASS_NAMES[classKey] || `Class ${classKey}`;
      const students = classData.students || [];
      return students.map(s => ({
        id: String(s.studentID),
        name: s.studentName || `Student ${s.studentID}`,
        className,
        section: s.section,
        academicYear: '2025-2026',
        attendancePercentage: s.workingDay > 0 ? +((s.presentDay / s.workingDay) * 100).toFixed(1) : 0,
        competencyLevel: s.marks >= 85 ? 'Excellent' : s.marks >= 70 ? 'Good' : s.marks >= 55 ? 'Average' : 'Below Average',
        behaviorScore: +(3.0 + (s.marks / 100) * 2.0).toFixed(1),
        peerScore: +(2.8 + (s.marks / 100) * 2.2).toFixed(1),
      }));
    });
  }, [schoolRes]);

  const classes = useMemo(() => Array.from(new Set(mockStudents.map(s => s.className))).sort(), [mockStudents]);
  const years = useMemo(() => Array.from(new Set(mockStudents.map(s => s.academicYear))), [mockStudents]);

  const filtered = useMemo(() => {
    return mockStudents.filter(s => {
      const nameMatch = s.name.toLowerCase().includes(search.toLowerCase()) || s.id.toLowerCase().includes(search.toLowerCase());
      const classMatch = !classFilter || s.className === classFilter;
      const yearMatch = !yearFilter || s.academicYear === yearFilter;
      return nameMatch && classMatch && yearMatch;
    });
  }, [mockStudents, search, classFilter, yearFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Loading student list…</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Student List — Secondary Stage</h1>
          <p className="text-sm text-gray-500">{filtered.length} students found (Class IX & XI)</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search by name or ID..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 w-full" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select value={classFilter} onChange={e => { setClassFilter(e.target.value); setPage(1); }} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
            <option value="">All Classes</option>
            {classes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={yearFilter} onChange={e => { setYearFilter(e.target.value); setPage(1); }} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
            <option value="">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Student</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Class</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Attendance</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Competency</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Behavior</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Peer Score</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((student) => (
                <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                        {student.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div><p className="text-sm font-medium text-gray-900">{student.name}</p></div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700">{student.className} {student.section}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-sm font-medium ${student.attendancePercentage >= 85 ? 'text-emerald-600' : student.attendancePercentage >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{student.attendancePercentage}%</span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${competencyColor[student.competencyLevel]}`}>{student.competencyLevel}</span>
                  </td>
                  <td className="px-5 py-3 text-center text-sm text-gray-700">{student.behaviorScore}</td>
                  <td className="px-5 py-3 text-center text-sm text-gray-700">{student.peerScore}</td>
                  <td className="px-5 py-3 text-center">
                    <button onClick={() => navigate(`student/${student.id}/overview`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition">
                      <Eye size={13} /> View Profile
                    </button>
                  </td>
                </tr>
              ))}
              {paginated.length === 0 && (<tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No students found</td></tr>)}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (<div className="px-2 py-2 border-t border-gray-100"><Pagination currentPage={page} totalPages={totalPages} totalRecords={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>)}
      </div>
    </div>
  );
};

export default StudentList;
